import { initialize } from "launchdarkly-js-client-sdk"
import type { LDFlagSet, LDOptions } from "launchdarkly-js-client-sdk"
import { useEffect, useState } from "react"

const anonymousUserContext = {
    kind: "user",
    key: "anonymous",
    anonymous: true,
    platform: "web",
}

/**
 * When the server injects a flag bootstrap dict (see Django's
 * ``LD_TEST_FLAGS_FILE`` setting), initialize the SDK with those values and
 * skip streaming + event uploads so the client makes no network requests to
 * LaunchDarkly. Otherwise behave exactly as before: stream from LD.
 */
const bootstrap = window.ld_flag_bootstrap
const clientOptions: LDOptions = bootstrap ? { bootstrap: bootstrap as LDFlagSet, streaming: false, sendEvents: false } : { sendEventsOnlyForVariation: true }

const featureFlagsClient = initialize(window.ld_client_id, anonymousUserContext, clientOptions)
Object.freeze(featureFlagsClient)

let ready = false
featureFlagsClient.on("ready", () => {
    ready = true
    if (process.env.NODE_ENV !== "production" && featureFlagsUsedBeforeInit.size > 0) {
        const flags = Array.from(featureFlagsUsedBeforeInit)
        flags.sort()
        console.error(
            "[LaunchDarkly] Feature flags were used before the client initialized.\n" +
                "  When this happens, the default value (e.g. `false`) is" +
                " returned instead of the flag status in LD.\n" +
                flags.map(flag => `  - ${flag}`).join("\n")
        )
    }
})

export const featureFlagsUsedBeforeInit = new Set<string>()
export const featureFlagsClientIsReady = (): boolean => ready

export const useFeatureFlagsClientIsReady = (): boolean => {
    const [isReady, setIsReady] = useState(false)
    useEffect(() => {
        if (featureFlagsClientIsReady()) {
            setIsReady(true)
            return
        }
        // The "ready" event will always be emitted by the client, whether it successfully initialized or not.
        // If the client did not initialize successfully, it will serve default feature flag values.
        featureFlagsClient.on("ready", () => {
            setIsReady(true)
        })
    }, [])
    return isReady
}

export default featureFlagsClient
